import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Form, InputNumber, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { InfiniteScroll, Spin } from 'common/components';
import { debounce } from 'common/helpers';
import { DEFAULT_VALUE_0, LIST_LIMIT_0 } from 'common/config';
import { IFormValue } from 'common/models';
import { Percentage, Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IContract, IUpdateContractPayload } from 'entities/Contract/Contract.models';
import { BrandDiscountCreateCard } from 'entities/Discount/components';
import { IDiscountBrand, IUpdateDiscountBrandPayload } from 'entities/Discount/Discount.models';
import { renderBrandDiscountListRecords } from 'entities/Settings/Settings.helper';
import { getBrands, selectBrandList, selectIsBrandLoading } from 'entities/Brand/Brand.slice';
import { selectIsContractLoading, updateContract } from 'entities/Contract/Contract.slice';
import {
  createDiscountBrand,
  deleteDiscountBrand,
  getDiscountBrandList,
  selectDiscountBrandList,
  selectIsDiscountLoading,
  updateDiscountBrand,
} from 'entities/Discount/Discount.slice';

interface IComponentProps {
  header: ReactNode;
  contract: IContract | null;
}

export const SettingsContractDiscountListTab: FC<IComponentProps> = ({ header, contract }) => {
  const dispatch = useAppDispatch();
  // brand
  const brandList = useAppSelector(selectBrandList);
  const isBrandLoading = useAppSelector(selectIsBrandLoading);
  // contract
  const isContractLoading = useAppSelector(selectIsContractLoading);
  // discount
  const discountBrandList = useAppSelector(selectDiscountBrandList);
  const isDiscountLoading = useAppSelector(selectIsDiscountLoading);

  const [selectedBrandDiscount, setSelectedBrandDiscount] = useState<IDiscountBrand | null>(null);
  const [openBrandDiscountCreateCard, setOpenBrandDiscountCreateCard] = useState<boolean>(false);
  const [form] = useForm();

  const contractBrands = discountBrandList.map((discount) => discount.brand);
  const brandListForView = brandList.filter((brand) => {
    return !contractBrands.some((contractBrand) => contractBrand.toLowerCase() === brand.toLowerCase());
  });

  const debouncedUpdateContract = debounce((payload: IUpdateContractPayload) => dispatch(updateContract(payload)));

  const onCreateBrandDiscountClick = (record?: IDiscountBrand) => {
    if (contract) {
      dispatch(
        getBrands({
          onSuccess: () => {
            setSelectedBrandDiscount(record ? record : ({ discountValue: contract.discountValue } as IDiscountBrand));
            setOpenBrandDiscountCreateCard(true);
          },
        }),
      );
    }
  };

  const onBrandDiscountCreateCardCreateClick = (brand: string, discountValue: number, onSuccess: () => void) => {
    if (contract) {
      dispatch(
        createDiscountBrand({
          contractId: contract.id,
          brand,
          discountValue,
          onSuccess,
        }),
      );
    }
  };

  const onBrandDiscountCreateCardUpdateClick = (payload: IUpdateDiscountBrandPayload) => {
    dispatch(updateDiscountBrand(payload));
  };

  const onBrandDiscountCreateCardDeleteClick = (id: number, onSuccess?: () => void) => {
    dispatch(deleteDiscountBrand({ id, onSuccess }));
  };

  const onFormValuesChange = ({ discountValue }: IFormValue) => {
    if (contract) {
      debouncedUpdateContract({ id: contract.id, discountValue: discountValue ? discountValue : DEFAULT_VALUE_0 });
    }
  };

  useEffect(() => {
    if (contract) {
      form.setFieldValue('discountValue', contract.discountValue);
      dispatch(getDiscountBrandList({ contractId: contract.id, limit: LIST_LIMIT_0 }));
    }
  }, [contract?.id]);

  return (
    <>
      <InfiniteScroll>
        {header}

        <div>
          <Spin spinning={isContractLoading || isDiscountLoading || isBrandLoading}>
            <div className="text-h4 mb-3">Базовая скидка на ассортимент</div>

            <div className="text-tag color-dark-grey mb-6" style={{ maxWidth: 800 }}>
              Базовая скидка применяется ко всем категориям товаров и брендам, установленным в разделе «Ассортимент» этого
              контракта. Базовая скидка не суммируется и не исключает другие типы скидок.
            </div>

            <Form form={form} layout="vertical" requiredMark={false} onValuesChange={onFormValuesChange}>
              <Form.Item label="Скидка на товары*" name="discountValue" className="mb-14" style={{ maxWidth: 360 }}>
                <InputNumber
                  placeholder="0"
                  suffix={<Percentage className="icon-percentage-dark-grey" />}
                  controls={false}
                  precision={0}
                />
              </Form.Item>
            </Form>

            <Button
              className="button-sm primary mb-6"
              icon={<Plus className="icon-plus-white" />}
              onClick={() => onCreateBrandDiscountClick()}
            >
              Скидка на бренд
            </Button>

            {!!discountBrandList?.length && (
              <Table
                className="mb-19 contract-discount-list-settings__table"
                showHeader={false}
                pagination={false}
                size="middle"
                dataSource={discountBrandList.map((discount) => ({ ...discount, key: discount.id }))}
                columns={renderBrandDiscountListRecords(onCreateBrandDiscountClick, onBrandDiscountCreateCardDeleteClick)}
              />
            )}
          </Spin>
        </div>
      </InfiniteScroll>

      <BrandDiscountCreateCard
        open={openBrandDiscountCreateCard}
        loading={isDiscountLoading}
        brands={brandListForView}
        brandDiscount={selectedBrandDiscount}
        onClose={() => setOpenBrandDiscountCreateCard(false)}
        onCreate={onBrandDiscountCreateCardCreateClick}
        onUpdate={onBrandDiscountCreateCardUpdateClick}
        onDelete={onBrandDiscountCreateCardDeleteClick}
      />
    </>
  );
};
